import { useState, useEffect, useCallback } from "react";
import {
  getCreditRequests, getCreditRequest, getCreditRequestImpact,
  managerReviewCredit, adminReviewCredit,
} from "../api";

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_MAP = {
  pending:          { label: "Pending",          cls: "badge-pending"  },
  manager_approved: { label: "Manager Approved", cls: "badge-medium"   },
  approved:         { label: "Approved",         cls: "badge-approved" },
  rejected:         { label: "Rejected",         cls: "badge-rejected" },
};

const IMP_COLOR = (s) =>
  s <= 3 ? "var(--success, #15803d)" : s <= 6 ? "var(--warning, #b45309)" : "var(--danger, #b91c1c)";
const IMP_BG = (s) =>
  s <= 3 ? "var(--success-light, #f0fdf4)" : s <= 6 ? "var(--warning-light, #fef3c7)" : "var(--danger-light, #fee2e2)";

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || { label: status, cls: "" };
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
}

function ImportanceBadge({ score }) {
  return (
    <span className="badge" style={{ background: IMP_BG(score), color: IMP_COLOR(score) }}>
      {score}/10
    </span>
  );
}

function fmt(n) { return n?.toLocaleString() ?? "—"; }
function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

// ── Usage history panel ───────────────────────────────────────────────────────

function UsageHistoryPanel({ history, creditHistory, loading }) {
  if (loading) return (
    <div className="text-muted" style={{ padding: "24px 0", textAlign: "center", fontSize: 14 }}>
      Loading usage history from Anthropic…
    </div>
  );

  const hasAny = history.some((p) => p.total_tokens > 0);

  // Map billing_period → impact_from_previous from credit history
  const impactByPeriod = {};
  (creditHistory || []).forEach((cr) => {
    if (cr.impact_from_previous) {
      const period = cr.created_at?.slice(0, 7); // "YYYY-MM"
      if (period && !impactByPeriod[period]) {
        impactByPeriod[period] = cr.impact_from_previous;
      }
    }
  });

  return (
    <div>
      <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 14px" }}>
        Claude Usage History (Last 3 Billing Cycles)
      </h3>

      {!hasAny && (
        <div className="card card-body text-muted" style={{ fontSize: 13 }}>
          No usage data found. This may be because the Anthropic Analytics API key is not yet
          configured, or this user has not used Claude during these periods.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {history.map((period) => {
          const impact = impactByPeriod[period.billing_period];
          return (
            <div key={period.billing_period} className="card" style={{ overflow: "hidden" }}>
              <div className="card-header" style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <span>{period.billing_period}</span>
                <span className="text-muted" style={{ fontSize: 12 }}>
                  {period.days_active} active day{period.days_active !== 1 ? "s" : ""}
                </span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)" }}>
                {[
                  { label: "Total Tokens",  value: fmt(period.total_tokens) },
                  { label: "Input Tokens",  value: fmt(period.input_tokens) },
                  { label: "Output Tokens", value: fmt(period.output_tokens) },
                  { label: "Credits Used",  value: period.total_cost_credits?.toFixed(2) ?? "—" },
                ].map(({ label, value }, i) => (
                  <div key={label} style={{
                    padding: "12px 16px",
                    borderRight: i < 3 ? "1px solid var(--border, #e2e8f0)" : "none",
                  }}>
                    <div className="text-muted" style={{ fontSize: 11, textTransform: "uppercase",
                      letterSpacing: "0.05em", marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, fontFamily: "monospace" }}>{value}</div>
                  </div>
                ))}
              </div>

              {period.model_breakdown && Object.keys(period.model_breakdown).length > 0 && (
                <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border, #e2e8f0)" }}>
                  <div className="text-muted" style={{ fontSize: 11, textTransform: "uppercase",
                    letterSpacing: "0.05em", marginBottom: 8 }}>By Model</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {Object.entries(period.model_breakdown).map(([model, stats]) => (
                      <div key={model} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                        <span style={{ fontFamily: "monospace", fontSize: 12, color: "var(--primary, #2563eb)" }}>{model}</span>
                        <span className="text-muted">
                          {fmt(stats.input_tokens + stats.output_tokens)} tokens
                          {stats.credits > 0 && ` · ${stats.credits.toFixed(3)} credits`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {impact && (
                <div style={{
                  padding: "10px 16px",
                  borderTop: "1px solid var(--border, #e2e8f0)",
                  background: "var(--warning-light, #fef3c7)",
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#92400e",
                    textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                    Impact from Previous Credits
                  </div>
                  <p style={{ margin: 0, fontSize: 13, color: "#78350f", lineHeight: 1.5 }}>{impact}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Detail view ───────────────────────────────────────────────────────────────

// Data-backed "what did they ship last time" card for reviewers — separate from
// the requester's own self-reported impact_from_previous text above. Computed
// from the requester's last approved cycle plus GitHub PRs / Jira tickets in
// that window (see GET /credit-requests/{id}/impact).
function ShippedWorkImpact({ impact, loading }) {
  if (loading) {
    return (
      <div className="card card-body text-muted" style={{ fontSize: 13 }}>
        Loading shipped-work impact…
      </div>
    );
  }
  if (!impact) return null;

  const label = (
    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--primary, #2563eb)", textTransform: "uppercase",
      letterSpacing: "0.05em", marginBottom: 8 }}>
      Shipped-Work Impact (from GitHub/Jira)
    </div>
  );

  if (!impact.available) {
    return (
      <div className="card card-body">
        {label}
        <p style={{ fontSize: 13, margin: 0, color: "var(--muted, #6b7280)", lineHeight: 1.6 }}>
          {impact.message}
        </p>
      </div>
    );
  }

  const items = [
    ...impact.merged_prs.map((pr) => ({ ...pr, kind: "PR", text: pr.title })),
    ...impact.resolved_tickets.map((t) => ({ ...t, kind: "Ticket", text: `${t.key} — ${t.summary}` })),
  ].sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  return (
    <div className="card card-body" style={{ background: "#eff6ff", border: "1px solid #bfdbfe" }}>
      {label}
      <p style={{ fontSize: 14, margin: "0 0 10px", lineHeight: 1.6, color: "#1e3a8a" }}>{impact.message}</p>
      {items.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {items.slice(0, 5).map((it, i) => (
            <a key={i} href={it.url} target="_blank" rel="noreferrer"
              style={{ fontSize: 12, color: "#1e40af", textDecoration: "none", display: "flex", gap: 6, alignItems: "baseline" }}>
              <span className="badge" style={{ background: "#dbeafe", color: "#1e40af", fontSize: 10, flexShrink: 0 }}>{it.kind}</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.text}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function CreditRequestDetail({ id, auth, onBack, onUpdated }) {
  const [data,         setData]         = useState(null);
  const [creditHist,   setCreditHist]   = useState([]);
  const [usageLoading, setUsageLoading] = useState(true);
  const [error,        setError]        = useState("");
  const [impact,        setImpact]        = useState(null);
  const [impactLoading, setImpactLoading] = useState(true);

  const [reviewNote,      setReviewNote]      = useState("");
  const [approvedCredits, setApprovedCredits] = useState("");
  const [submitting,      setSubmitting]      = useState(false);
  const [reviewError,     setReviewError]     = useState("");

  const load = useCallback(async () => {
    setUsageLoading(true);
    setImpactLoading(true);
    try {
      const res = await getCreditRequest(id);
      setData(res);
      // Also fetch prior credit requests for this user to show impact in history panel
      if (res?.request?.requester_email) {
        getCreditRequests({ requester_email: res.request.requester_email })
          .then(setCreditHist)
          .catch(() => {});
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUsageLoading(false);
    }
    getCreditRequestImpact(id)
      .then(setImpact)
      .catch(() => setImpact(null))
      .finally(() => setImpactLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (error) return (
    <div className="page-body">
      <button onClick={onBack} className="btn btn-outline btn-sm" style={{ marginBottom: 16 }}>← Back</button>
      <div className="error-msg">{error}</div>
    </div>
  );

  if (!data) return (
    <div className="page-body text-muted">Loading…</div>
  );

  const req   = data.request;
  const history = data.usage_history || [];

  const isAdmin = auth.role === "admin";
  const isMgr   = auth.role === "manager";

  // Admin CANNOT do step 1; only assigned manager
  const canMgrReview   = isMgr && req.status === "pending" && auth.email === req.manager_email;
  // Final approval is admin-only, even for a manager's own reportees — a manager
  // may only perform step 1 (the manager review above).
  const canAdminReview = isAdmin && req.status === "manager_approved";

  const handleReview = async (action, isAdminStep) => {
    setSubmitting(true);
    setReviewError("");
    try {
      const payload = {
        action,
        reviewer_name:  auth.name,
        reviewer_email: auth.email,
        review_note:    reviewNote || null,
        ...(isAdminStep && action === "approve" && approvedCredits
          ? { approved_credits: parseFloat(approvedCredits) }
          : {}),
      };
      if (isAdminStep) {
        await adminReviewCredit(id, payload);
      } else {
        await managerReviewCredit(id, payload);
      }
      onUpdated?.();
      load();
    } catch (err) {
      setReviewError(err.message || "Review submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="page-header" style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <button onClick={onBack} className="btn btn-outline btn-sm">← Back</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 20 }}>{req.project_name}</h1>
          <p style={{ margin: "4px 0 0" }}>
            {req.requester_name} · {req.requester_email} · {fmtDate(req.created_at)}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <ImportanceBadge score={req.importance_score} />
          <StatusBadge status={req.status} />
        </div>
      </div>

      <div className="page-body">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>

          {/* Left column */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Key numbers */}
            <div className="card" style={{ overflow: "hidden" }}>
              <div className="card-header">Request Details</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                {[
                  { label: "Credits Requested", value: fmt(req.credits_requested), mono: true },
                  { label: "Team",              value: req.team || "—" },
                  { label: "Department",        value: req.department || "—" },
                  { label: "Approved Credits",  value: req.approved_credits ? fmt(req.approved_credits) : "—", mono: true },
                ].map(({ label, value, mono }, i) => (
                  <div key={label} style={{
                    padding: "12px 16px",
                    borderRight: i % 2 === 0 ? "1px solid var(--border, #e2e8f0)" : "none",
                    borderBottom: i < 2 ? "1px solid var(--border, #e2e8f0)" : "none",
                  }}>
                    <div className="text-muted" style={{ fontSize: 11, textTransform: "uppercase",
                      letterSpacing: "0.05em", marginBottom: 4 }}>{label}</div>
                    <div style={{ fontWeight: 700, fontSize: 15, fontFamily: mono ? "monospace" : "inherit" }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Business justification */}
            <div className="card card-body">
              <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase",
                letterSpacing: "0.05em", marginBottom: 8 }}>Business Justification</div>
              <p style={{ fontSize: 14, margin: 0, lineHeight: 1.6 }}>{req.business_justification}</p>
            </div>

            {/* Impact — requester's own account */}
            {req.impact_from_previous && (
              <div className="card card-body" style={{ background: "var(--warning-light, #fef3c7)", border: "1px solid #fde68a" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#92400e", textTransform: "uppercase",
                  letterSpacing: "0.05em", marginBottom: 8 }}>Impact from Previous Credits (self-reported)</div>
                <p style={{ fontSize: 14, margin: 0, lineHeight: 1.6, color: "#78350f" }}>{req.impact_from_previous}</p>
              </div>
            )}

            {/* Impact — computed from GitHub/Jira, for reviewers */}
            {(isAdmin || isMgr) && (
              <ShippedWorkImpact impact={impact} loading={impactLoading} />
            )}

            {/* Approval trail */}
            {(req.manager_reviewed_at || req.admin_reviewed_at) && (
              <div className="card card-body">
                <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase",
                  letterSpacing: "0.05em", marginBottom: 12 }}>Approval Trail</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {req.manager_reviewed_at && (
                    <div style={{ fontSize: 13 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                        <span style={{ fontWeight: 600 }}>Manager: {req.manager_name}</span>
                        <StatusBadge status={req.status === "pending" ? "rejected" : "manager_approved"} />
                      </div>
                      <div className="text-muted">{fmtDate(req.manager_reviewed_at)}</div>
                      {req.manager_review_note && (
                        <div style={{ marginTop: 4, fontStyle: "italic", color: "#4b5563" }}>
                          "{req.manager_review_note}"
                        </div>
                      )}
                    </div>
                  )}
                  {req.admin_reviewed_at && (
                    <div style={{ fontSize: 13 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                        <span style={{ fontWeight: 600 }}>Final Review: {req.admin_reviewer_name}</span>
                        <StatusBadge status={req.status} />
                      </div>
                      <div className="text-muted">{fmtDate(req.admin_reviewed_at)}</div>
                      {req.admin_review_note && (
                        <div style={{ marginTop: 4, fontStyle: "italic", color: "#4b5563" }}>
                          "{req.admin_review_note}"
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Review form */}
            {(canMgrReview || canAdminReview) && (
              <div className="card card-body" style={{ border: "1px solid #a5b4fc" }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: "var(--primary, #2563eb)" }}>
                  {canAdminReview ? "Final Approval" : "Manager Review (Step 1 of 2)"}
                </div>

                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12 }}>Review Note (optional)</label>
                  <textarea rows={3} value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    placeholder="Add a note for the requester…"
                    style={{ fontSize: 13 }} />
                </div>

                {canAdminReview && (
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 12 }}>
                      Approved Credits (leave blank to grant full requested amount)
                    </label>
                    <input type="number" min="0" step="any"
                      value={approvedCredits}
                      onChange={(e) => setApprovedCredits(e.target.value)}
                      placeholder={`${req.credits_requested}`}
                      style={{ fontSize: 13 }} />
                  </div>
                )}

                {reviewError && (
                  <div className="error-msg" style={{ marginBottom: 10 }}>{reviewError}</div>
                )}

                <div style={{ display: "flex", gap: 10 }}>
                  <button className="btn btn-success" disabled={submitting}
                    onClick={() => handleReview("approve", canAdminReview)}>
                    {submitting ? "…" : canAdminReview ? "Approve & Grant" : "Approve → Next Step"}
                  </button>
                  <button className="btn btn-danger btn-sm" disabled={submitting}
                    onClick={() => handleReview("reject", canAdminReview)}>
                    {submitting ? "…" : "Reject"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right column: usage history */}
          <div>
            <UsageHistoryPanel history={history} creditHistory={creditHist} loading={usageLoading} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── List view ─────────────────────────────────────────────────────────────────

const STATUS_TABS = [
  { key: "",                 label: "All"      },
  { key: "pending",          label: "Pending"  },
  { key: "manager_approved", label: "At Admin" },
  { key: "approved",         label: "Approved" },
  { key: "rejected",         label: "Rejected" },
];

export default function CreditRequests({ auth, initialStatusTab = "" }) {
  const [requests,   setRequests]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [statusTab,  setStatusTab]  = useState(initialStatusTab);
  const [sortBy,     setSortBy]     = useState("importance_desc");
  const [view,       setView]       = useState("me"); // "me" | "team"
  const [error,      setError]      = useState("");

  const isAdmin = auth?.role === "admin";
  const isMgr   = auth?.role === "manager";

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { sort_by: sortBy };
      if (statusTab) params.status = statusTab;

      if (isAdmin) {
        // Admin sees all
      } else if (isMgr) {
        if (view === "team") {
          params.manager_email = auth.email;
        } else {
          params.requester_email = auth.email;
        }
      } else {
        params.requester_email = auth.email;
      }

      const res = await getCreditRequests(params);
      setRequests(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusTab, sortBy, view, auth, isAdmin, isMgr]);

  useEffect(() => { load(); }, [load]);

  if (selectedId) {
    return (
      <CreditRequestDetail
        id={selectedId}
        auth={auth}
        onBack={() => setSelectedId(null)}
        onUpdated={load}
      />
    );
  }

  const subtitle = isAdmin
    ? "All credit requests across the organisation."
    : isMgr && view === "team"
      ? "Credit requests submitted by your team members."
      : "Your submitted credit requests.";

  return (
    <div>
      <div className="page-header">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1>Credit Requests</h1>
            <p>{subtitle}</p>
          </div>

          {/* Manager toggle */}
          {isMgr && (
            <div style={{ display: "flex", background: "var(--border)", borderRadius: 8, padding: 3, gap: 3 }}>
              {[
                ["me",   "My Requests"],
                ["team", "Team Requests"],
              ].map(([v, label]) => (
                <button key={v} onClick={() => setView(v)} style={{
                  padding: "7px 16px", borderRadius: 6, border: "none", cursor: "pointer",
                  fontWeight: 600, fontSize: 13, fontFamily: "inherit",
                  background: view === v ? "var(--surface)" : "transparent",
                  color: view === v ? "var(--primary)" : "var(--muted)",
                  boxShadow: view === v ? "var(--shadow)" : "none",
                  transition: ".15s",
                }}>{label}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="page-body">
        {/* Tab bar + sort */}
        <div className="header-row" style={{ marginBottom: 0 }}>
          <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border, #e2e8f0)", flex: 1 }}>
            {STATUS_TABS.map((t) => (
              <button key={t.key}
                onClick={() => setStatusTab(t.key)}
                style={{
                  padding: "8px 16px", fontSize: 13, background: "none", border: "none",
                  cursor: "pointer", fontFamily: "inherit",
                  fontWeight: statusTab === t.key ? 700 : 400,
                  borderBottom: statusTab === t.key ? "2px solid var(--primary, #2563eb)" : "2px solid transparent",
                  color: statusTab === t.key ? "var(--primary, #2563eb)" : "var(--muted, #6b7280)",
                  marginBottom: -1,
                }}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="filters" style={{ marginLeft: 16 }}>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
              style={{ width: "auto", fontSize: 13, padding: "6px 10px" }}>
              <option value="importance_desc">Importance ↓</option>
              <option value="date_desc">Newest first</option>
            </select>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

          {loading ? (
            <div className="text-muted" style={{ padding: "20px 0" }}>Loading…</div>
          ) : requests.length === 0 ? (
            <div className="empty">
              <div style={{ fontSize: 32, marginBottom: 12 }}>💳</div>
              <div>No credit requests found.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {requests.map((req) => (
                <div key={req.id}
                  className="card"
                  onClick={() => setSelectedId(req.id)}
                  style={{
                    padding: "14px 18px",
                    cursor: "pointer",
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 12,
                    alignItems: "center",
                    transition: "box-shadow .12s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"}
                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = ""}
                >
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, fontSize: 15 }}>{req.project_name}</span>
                      <StatusBadge status={req.status} />
                    </div>
                    <div className="text-muted">
                      {req.requester_name} · {req.team || "—"} · {fmt(req.credits_requested)} credits · {fmtDate(req.created_at)}
                    </div>
                    {req.business_justification && (
                      <div style={{
                        fontSize: 12, color: "var(--muted, #9ca3af)", marginTop: 4,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 520,
                      }}>
                        {req.business_justification}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                    <ImportanceBadge score={req.importance_score} />
                    <span className="text-muted" style={{ fontSize: 11 }}>#{req.id}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
